import React from 'react'
import { useSelector } from 'react-redux'
import { editorSelectors } from 'store/features/editor/selectors'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'

import UploadIcon from 'assets/svgr-icons/Upload.svg'
import MyFilesIcon from 'assets/svgr-icons/My_Files.svg'
import { ButtonWithIcon, Text } from 'common-components'
import MediaUploadButton from 'components/MediaUploadButton'
import RecentlyUsed from 'components/RecentlyUsed'
import useDurationLimitCheck from 'hooks/useDurationLimitCheck'
import { IMediaItem, IMyFilesMediaItem, IUploadMediaItem } from 'types/media'
import {
  calculateTotalMediaDuration,
  convertMediaLibraryItemsToMediaItems
} from 'utils/media'
import { translate } from 'utils/translations'

import * as Styled from './styled'
import { T } from 'consts/translationKeys'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { dispatch } from 'store'
import { recentlyUsedMediaThunks } from 'store/features/recentlyUsedMedia/thunks'
import { scrollToElementById } from 'utils'
import { editorThunks } from 'store/features/editor/thunks'
import { useProjectMediaUpload } from 'hooks/project-item-upload'
import { useInsertionStartTime } from 'hooks/timeline/useInsertionStartTime'
import { assignStartTimes } from 'hooks/project-item-upload/use-project-media-upload/helpers'

const MediaContent: React.FC = () => {
  const activeTab = useSelector(editorSelectors.activeSidebarTab)
  const mediaItemsDuration = useSelector(
    projectDataSelectors.mediaItemsDuration
  )
  const { width: projectDefaultWidth, height: projectDefaultHeight } =
    useSelector(projectDataSelectors.defaultDimensions)

  const getInsertionStartTime = useInsertionStartTime()

  const { isDurationAllowed } = useDurationLimitCheck()

  const handleChoose = async (files: IMyFilesMediaItem[]) => {
    // Check if total file size exceeds available storage

    const mediaFiles = convertMediaLibraryItemsToMediaItems(
      files,
      mediaItemsDuration,
      { width: projectDefaultWidth, height: projectDefaultHeight }
    )
    const totalNewDuration = calculateTotalMediaDuration(mediaFiles)

    if (!isDurationAllowed(totalNewDuration)) {
      return
    }

    const insertionStartTime = getInsertionStartTime(totalNewDuration)

    const positionedMediaItems = assignStartTimes(
      mediaFiles,
      insertionStartTime
    )

    dispatch(
      projectDataThunks.uploadFromMediaLibrary({
        value: positionedMediaItems
      })
    )
    scrollToElementById(mediaFiles[0].id)
    dispatch(
      recentlyUsedMediaThunks.updateRecentlyUsedItemsOnInsertFromMediaLibrary(
        mediaFiles
      )
    )
  }

  const onAddMediaItems = (mediaFiles: IMediaItem[]) => {
    dispatch(projectDataThunks.addMediaItems({ value: mediaFiles }))
  }

  const onUploadMedias = (uploadItems: IUploadMediaItem[]) => {
    dispatch(projectDataThunks.uploadMediasToProject(uploadItems))
    dispatch(editorThunks.setIsMobileSubSidebarOpen(false))
  }

  const handleFilesUpload = useProjectMediaUpload(
    onAddMediaItems,
    onUploadMedias
  )

  const onMyFilesClick = () => {
    window.openMediaLib({
      fileType: 'visualMedia',
      onClose: () => {
        if (activeTab === SUB_SIDEBAR_KEYS.UPLOAD) {
          window.closeMediaLib()
          // Filter recently used items after media library closes to remove deleted images
          dispatch(recentlyUsedMediaThunks.filterAndUpdateRecentlyUsedItems())
        }
      },
      mediaLibUnmountCb: window.closeMediaLib,
      onChoose: handleChoose,
      maxFileImportCount: 10, // TODO check real max count
      isVisible: true,
      stockAllowed: false
    })
  }

  return (
    <Styled.MediaContentContainer>
      <Text type="h1" color="secondaryDark">
        {translate(T.ADD_MEDIA_LABEL)}
      </Text>
      <Styled.ButtonsGroup>
        <MediaUploadButton
          acceptType="visualMedia"
          handleFilesUpload={handleFilesUpload}
        >
          <ButtonWithIcon
            icon={<UploadIcon />}
            label={translate(T.UPLOAD_LABEL)}
          />
        </MediaUploadButton>
        <ButtonWithIcon
          icon={<MyFilesIcon />}
          onClick={onMyFilesClick}
          label={translate(T.MY_FILES_LABEL)}
        />
      </Styled.ButtonsGroup>
      <RecentlyUsed />
    </Styled.MediaContentContainer>
  )
}

export default MediaContent
